import { coral, green, grey25 } from '@zocdoc/branding/colors';
import { ThemedInsurancePicker as BaseInsurancePicker } from '@zocdoc/components/src/InsurancePicker';
import { Link } from '@zocdoc/components/src/link';
import { FontFamily } from '@zocdoc/components/src/typography';
import PropTypes from 'prop-types';
import { stringify } from 'querystring';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { withTheme } from 'styled-components';

import * as TrackingConstants from '../../../../EventTrackingConstants';
import InsuranceList from '../../../../Shared/InsuranceList';
import InNetworkInsuranceModule from '../../../../Shared/InNetworkInsuranceModule';
import addInsuranceModal from '../../../../Shared/Modals/addInsuranceModal';
import { insuranceTypeFromProvider } from '../../../../utils/insuranceTypeFrom';
import isZocdocBrand from '../../../../utils/isZocdocBrand';
import { PROFILE_GA_CATEGORY } from '../../constants';
import { LOCALIZED_STRINGS } from './constants';
import InNetworkInsurancePreview from './InNetworkInsurancePreview';

const InsurancePicker = addInsuranceModal(BaseInsurancePicker);

export class InlineInNetworkInsurance extends React.PureComponent {
    static propTypes = {
        ab: PropTypes.object,
        carrierId: PropTypes.string,
        carrierName: PropTypes.string,
        changeInsurance: PropTypes.func.isRequired,
        clickBookAnyway: PropTypes.func.isRequired,
        clickChangeInsurance: PropTypes.func.isRequired,
        detailsVisible: PropTypes.bool,
        hideInsuranceDetails: PropTypes.func.isRequired,
        initParameters: PropTypes.object,
        inNetworkInsurance: PropTypes.arrayOf(PropTypes.object).isRequired,
        insuranceIsAccepted: PropTypes.bool,
        isDesktop: PropTypes.bool,
        planId: PropTypes.string,
        planName: PropTypes.string,
        provider: PropTypes.shape({
            nameInSentence: PropTypes.string,
            providerId: PropTypes.string,
            specialties: PropTypes.array,
        }),
        showInsuranceDetails: PropTypes.func.isRequired,
        trackClickShowInNetworkDoctors: PropTypes.func.isRequired,
    };

    render() {
        const { isDesktop, theme: { directoryId } } = this.props;
        const is1Z = isZocdocBrand(directoryId);

        if (isDesktop && is1Z) {
            return this.renderForDesktopProfile1Z();
        } else if (is1Z) {
            return this.renderMobileForProfile1Z();
        } else {
            return this.renderForProfile();
        }
    }

    renderMobileForProfile1Z() {
        const {
            clickChangeInsurance,
            clickBookAnyway,
            carrierId,
            planId,
            carrierName,
            planName,
            bookingSpotlightIsOn,
            provider,
        } = this.props;
        return (
            <div>
                <InsurancePickerLabel>
                    <FormattedMessage {...LOCALIZED_STRINGS.InsuranceCarrierAndPlan} />
                </InsurancePickerLabel>

                <StyledInsurancePickerContainer>
                    <InsurancePicker
                        bookingSpotlightIsOn={bookingSpotlightIsOn}
                        carrierId={carrierId}
                        carrierName={carrierName}
                        data-test="wrapped-insurance-picker-profile-section"
                        gaCategory={PROFILE_GA_CATEGORY}
                        hideValidation={false}
                        insuranceType={insuranceTypeFromProvider(provider)}
                        onChange={this.handleInsuranceChange}
                        onClickBookAnyway={clickBookAnyway}
                        onClickChangeInsurance={clickChangeInsurance}
                        planId={planId}
                        planName={planName}
                        providerId={provider.id}
                        providerName={provider.nameInSentence}
                        section={TrackingConstants.TARGETS.BOOK_AN_APPOINTMENT}
                    />
                </StyledInsurancePickerContainer>
                {this.maybeRenderValidation()}
            </div>
        );
    }

    maybeRenderValidation() {
        const { trackClickShowInNetworkDoctors, insuranceIsAccepted } = this.props;

        if (insuranceIsAccepted === true) {
            return (
                <AcceptsValidationSentence data-test="validation-sentence-doctor-accepts">
                    <FormattedMessage {...LOCALIZED_STRINGS.DoctorAccepts} />
                </AcceptsValidationSentence>
            );
        } else if (insuranceIsAccepted === false) {
            return (
                <div>
                    <DoesNotAcceptValidationSentence data-test="validation-sentence-doctor-does-not-accept">
                        <FormattedMessage {...LOCALIZED_STRINGS.DoctorDoesNotAccept} />
                    </DoesNotAcceptValidationSentence>
                    <InNetworkDoctorsLink
                        beforeClick={trackClickShowInNetworkDoctors}
                        href={this.buildSearchRedirectUrl()}
                    >
                        <FormattedMessage {...LOCALIZED_STRINGS.ShowInNetworkDoctors} />
                    </InNetworkDoctorsLink>
                </div>
            );
        } else {
            return null;
        }
    }

    handleInsuranceChange = insurance => {
        const payload = {};
        if (insurance.carrier) {
            payload.carrierId = insurance.carrier.id;

            if (insurance.plan) {
                payload.planId = insurance.plan.id;
            }

            this.props.changeInsurance(payload);
        }
    };

    renderForDesktopProfile1Z() {
        const {
            detailsVisible,
            hideInsuranceDetails,
            inNetworkInsurance,
            showInsuranceDetails,
        } = this.props;

        return (
            <InNetworkInsuranceModule
                insurances={inNetworkInsurance}
                onModalDismissed={hideInsuranceDetails}
                showDetails={detailsVisible}
                viewAll={showInsuranceDetails}
            />
        );
    }

    renderForProfile() {
        const {
            detailsVisible,
            inNetworkInsurance,
        } = this.props;

        let contents;
        if (detailsVisible) {
            contents = (
                <InsuranceList
                    data-test="in-network-insurance-details"
                    insurances={inNetworkInsurance}
                />
            );
        } else {
            contents = (
                <InNetworkInsurancePreview
                    data-test="in-network-insurance-preview"
                    inNetworkInsurance={inNetworkInsurance}
                />
            );
        }

        let topViewLessLinkButton;
        if (inNetworkInsurance && inNetworkInsurance.length > 9) {
            topViewLessLinkButton = this.maybeRenderViewLess();
        }

        return (
            <div>
                {topViewLessLinkButton}
                {contents}
                {this.maybeRenderViewMore()}
                {this.maybeRenderViewLess()}
            </div>
        );
    }

    maybeRenderViewMore() {
        const { detailsVisible, showInsuranceDetails } = this.props;
        if (!detailsVisible) {
            return (
                <ShowMoreButton
                    data-test="show-insurance-details"
                    onClick={showInsuranceDetails}
                >
                    <FormattedMessage {...LOCALIZED_STRINGS.ViewMore} />
                </ShowMoreButton>
            );
        }
    }

    maybeRenderViewLess() {
        const { detailsVisible, hideInsuranceDetails } = this.props;
        if (detailsVisible) {
            return (
                <ShowFewerButton
                    data-test="hide-insurance-details"
                    onClick={hideInsuranceDetails}
                >
                    <FormattedMessage {...LOCALIZED_STRINGS.ViewLess} />
                </ShowFewerButton>
            );
        }
    }

    buildSearchRedirectUrl = () => {
        const { carrierId, initParameters, planId } = this.props;
        const params = {
            insurance_carrier: carrierId,
            insurance_plan: planId,
        };

        if (initParameters.specialtyId && initParameters.specialtyId !== '-1') {
            params.dr_specialty = initParameters.specialtyId;
        }

        return `/search?${stringify(params)}`;
    }
}

const StyledInsurancePickerContainer = styled.div`
    margin-top: 5px;
    border: 1px solid ${grey25};
`;

const ShowMoreButton = styled(Link)`
    font: 18px/24px ${FontFamily.KievitSlabBook};
    padding: 0;
`;

const ShowFewerButton = styled(Link)`
    font: 18px/28px ${FontFamily.SharpSansSemibold};
    margin: 8px 28px;
`;

const ValidationSentence = styled.div`
    font: 13px/1.54 ${FontFamily.SharpSansSemibold};
    margin: 3px 0;
`;

const AcceptsValidationSentence = styled(ValidationSentence)`
    color: ${green};
`;

const DoesNotAcceptValidationSentence = styled(ValidationSentence)`
    color: ${coral};
`;

const InsurancePickerLabel = styled.div`
    font: 13px/1.54 ${FontFamily.SharpSansSemibold};
    margin-top: 8px;
`;

const InNetworkDoctorsLink = styled(Link)`
    display: inline-block;
    margin-top: 12px;
`;

export default withTheme(InlineInNetworkInsurance);
